import {
  RouteDriverSwapDTO,
  RouteFieldTesterSuggestionDTO,
  RoutePackageDTO,
} from 'api/model';
import { useGoogleMapsContext } from 'components/map/GoogleMapsContext';
import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { arcApex, arcBulges, arcPath, LatLng } from './route-map-geometry';
import { GapChoice, PackageSelection } from './selection-helpers';

const Wrapper = styled.div`
  position: sticky;
  top: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MapFrame = styled.div`
  position: relative;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 420px;
  border: 1px solid #b5b3b35c;
  border-radius: 4px;
`;

/** Vem kör vad vid ett skifte — färgen på kartan räcker inte för att veta vem som är vem. */
const SwapLegend = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #b5b3b35c;
  border-radius: 4px;
  font-size: 0.8em;
  pointer-events: none;
`;

const SwapLegendRow = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Swatch = styled.span<{ color: string }>`
  display: inline-block;
  width: 14px;
  height: 4px;
  border-radius: 2px;
  background: ${({ color }) => color};
`;

const Hint = styled.span`
  font-size: 0.8em;
  color: rgb(120, 120, 120);
`;

const swedenCenter = { lat: 59.5, lng: 16.0 };

/** Uppdragsbenen — bilar vi själva kör. */
const LEG_COLOR = 'rgb(204,102,51)';

/** Persontransport: föraren åker kollektivt, inget fordon flyttas. */
const TRANSIT_COLOR = 'rgb(52,102,180)';

/** Annonsbil eller utfyllnadsuppdrag: en riktig transport, men inte det här paketets. */
const REMEDY_ASSIGNMENT_COLOR = 'rgb(26,128,86)';

/** Skiftespartnerns rutt — samma dag, motsatt riktning. */
const SWAP_PARTNER_COLOR = 'rgb(120,90,170)';

const toLatLng = (lat?: number | null, lng?: number | null): LatLng | null =>
  lat != null && lng != null ? { lat: Number(lat), lng: Number(lng) } : null;

/**
 * En sträcka på kartan: ett uppdragsben, ett valt lucka-alternativ eller ett av
 * skiftespartnerns ben. Alla ritas som varsin polyline så att de kan få varsin båge —
 * ritas de i samma linje (som tidigare) hamnar ett returben ovanpå utresan.
 */
interface MapSegment {
  from: LatLng;
  to: LatLng;
  color: string;
  /** Streckad: sträckan flyttar inte det här paketets bil, så den ska inte läsas som ett ben. */
  dashed?: boolean;
  strokeOpacity?: number;
  /**
   * Brickan på bågens krön: bennumret i LegTimeline på paketets egna ben, eller fordonets
   * regnummer på skiftets halvor — där är det bilen som byter förare, inte benet som har ett nummer.
   */
  badge?: string;
  title?: string;
}

/** Skiftets sidor sett från det hovrade paketet: `mine` är paketets egen förare. */
const swapSidesFor = (swap: RouteDriverSwapDTO, packageIndex: number) =>
  swap.sideA.packageIndex === packageIndex
    ? { mine: swap.sideA, other: swap.sideB }
    : { mine: swap.sideB, other: swap.sideA };

const findSwap = (driverSwaps: RouteDriverSwapDTO[] | undefined, packageIndex: number) =>
  (driverSwaps ?? []).find(
    (s) => s.sideA.packageIndex === packageIndex || s.sideB.packageIndex === packageIndex
  );

/**
 * Ändpunkterna för ett skifte som ritas delat vid bytespunkten — eller null, och då ritas
 * skiftet som förut med motpartens hela rutt bredvid. Delningen kräver två enbenspaket och
 * koordinater på alla fem punkterna. Kartan och legenden frågar samma funktion, så att legenden
 * aldrig beskriver en delning som inte ritas.
 */
const splitSwapPoints = (
  pkg: RoutePackageDTO,
  partner: RoutePackageDTO | undefined,
  meetingPoint: LatLng | null
) => {
  if (!meetingPoint || pkg.legs.length !== 1 || partner?.legs.length !== 1) return null;
  const [myLeg] = pkg.legs;
  const [partnerLeg] = partner.legs;
  const myFrom = toLatLng(myLeg.fromLatitude, myLeg.fromLongitude);
  const myTo = toLatLng(myLeg.toLatitude, myLeg.toLongitude);
  const partnerFrom = toLatLng(partnerLeg.fromLatitude, partnerLeg.fromLongitude);
  const partnerTo = toLatLng(partnerLeg.toLatitude, partnerLeg.toLongitude);
  return myFrom && myTo && partnerFrom && partnerTo
    ? { myFrom, myTo, partnerFrom, partnerTo }
    : null;
};

/**
 * Sträckan som ett valt lucka-alternativ innebär. Alternativen delar INTE ändpunkter:
 * persontransporten går mellan förarens punkt och paketet, medan annons och utfyllnad går
 * sin egen väg (logistikcenter → anläggning respektive utfyllnadsuppdragets egna adresser).
 */
const gapLegFor = (
  choice: GapChoice,
  gap: RouteFieldTesterSuggestionDTO['firstLegGap'],
  transitFrom: LatLng | null,
  transitTo: LatLng | null
): MapSegment | null => {
  if (!gap || choice === 'none') return null;

  if (choice === 'personalTransport' && gap.personalTransport && transitFrom && transitTo) {
    return { from: transitFrom, to: transitTo, color: TRANSIT_COLOR, dashed: true };
  }

  if (choice === 'assignmentRequest' && gap.assignmentRequest) {
    const from = toLatLng(gap.assignmentRequest.fromLatitude, gap.assignmentRequest.fromLongitude);
    const to = toLatLng(gap.assignmentRequest.toLatitude, gap.assignmentRequest.toLongitude);
    return from && to
      ? { from, to, color: REMEDY_ASSIGNMENT_COLOR, dashed: true }
      : null;
  }

  if (choice === 'filler' && gap.filler) {
    const from = toLatLng(gap.filler.topCandidateFromLatitude, gap.filler.topCandidateFromLongitude);
    const to = toLatLng(gap.filler.topCandidateToLatitude, gap.filler.topCandidateToLongitude);
    return from && to
      ? { from, to, color: REMEDY_ASSIGNMENT_COLOR, dashed: true }
      : null;
  }

  return null;
};

interface Props {
  packages: RoutePackageDTO[];
  hoveredPackageIndex: number | null;
  /** Behövs för att veta vilket lucka-alternativ som är valt — bara det valda ritas. */
  selections?: Record<number, PackageSelection>;
  /** Skiftestransporter i förslaget — motpartens rutt och bytespunkten ritas med. */
  driverSwaps?: RouteDriverSwapDTO[];
}

/**
 * Schematisk karta som visar ETT paket i taget — det som hovras. Utan hover är
 * kartan tom (med 90 paket blir "visa allt" bara en orange stjärnexplosion).
 * Linjerna är fågelvägar mellan benens punkter, inte vägnätet.
 */
const RouteMap: React.FC<Props> = ({
  packages,
  hoveredPackageIndex,
  selections,
  driverSwaps,
}) => {
  const { scriptLoaded, createMap } = useGoogleMapsContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const overlaysRef = useRef<(google.maps.Polyline | google.maps.Marker)[]>([]);

  useEffect(() => {
    if (scriptLoaded && containerRef.current && !mapRef.current) {
      mapRef.current =
        createMap(
          containerRef.current,
          5,
          new google.maps.LatLng(swedenCenter.lat, swedenCenter.lng),
          false
        ) ?? null;
    }
  }, [scriptLoaded, createMap]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    overlaysRef.current.forEach((o) => o.setMap(null));
    overlaysRef.current = [];

    if (hoveredPackageIndex === null) return;
    const pkg = packages.find((p) => p.packageIndex === hoveredPackageIndex);
    if (!pkg) return;

    // Paketets punkter i ordning — grunden för markörerna och ändpunkter för lucka-benen.
    const path: LatLng[] = [];
    pkg.legs.forEach((leg) => {
      const from = toLatLng(leg.fromLatitude, leg.fromLongitude);
      const to = toLatLng(leg.toLatitude, leg.toLongitude);
      if (from) path.push(from);
      if (to) path.push(to);
    });
    if (path.length < 2) return;

    // ── Bennummer: samma siffra som LegTimeline sätter på uppdragsraden ───────────────
    // Pilarna visar riktning men inte ordning, och när ett paket har flera ben går det annars
    // inte att se vilken rad i kortet som hör till vilken sträcka.
    //
    // Samma villkor som LegTimeline: vid ett skifte på ett enbenspaket ritar kortet i stället två
    // onumrerade halvor (ut till bytespunkten, hem med motpartens fordon), och benet hör inte ihop
    // med någondera. En siffra här hade pekat på en rad som inte finns.
    const swap = findSwap(driverSwaps, pkg.packageIndex);
    const numbered = !(swap && pkg.legs.length === 1);
    const sides = swap ? swapSidesFor(swap, pkg.packageIndex) : undefined;
    const partner = sides
      ? packages.find((p) => p.packageIndex === sides.other.packageIndex)
      : undefined;
    const meetingPoint = swap ? toLatLng(swap.pointLatitude, swap.pointLongitude) : null;

    // ── Skiftet delat vid bytespunkten ───────────────────────────────────────────────
    // Ett skifte är två enbenspaket i motsatt riktning. Ritat som två hela ben såg det ut som om
    // båda förarna körde hela vägen; i verkligheten kör var och en sitt eget fordon till
    // bytespunkten och motpartens fordon hem. Därför fyra halvor, färgade efter VEM som kör, med
    // fordonets regnummer på varje halva så att man ser bilen byta förare.
    const myLeg = pkg.legs[0];
    const splitSwap = sides ? splitSwapPoints(pkg, partner, meetingPoint) : null;

    // Föraren slutar dagen där motpartens fordon ska, inte där det egna fordonet ska.
    if (splitSwap) path.splice(0, path.length, splitSwap.myFrom, splitSwap.partnerTo);

    // ── Sträckorna som ska ritas ─────────────────────────────────────────────────────
    // En polyline PER BEN. Tidigare ritades hela paketet som en enda linje, vilket gjorde att ett
    // returben lade sig exakt ovanpå utresan — linjen, pilarna och sifferbrickan doldes.
    const segments: MapSegment[] = [];

    if (splitSwap && sides && meetingPoint) {
      const { mine, other } = sides;
      const myName = mine.fieldTesterName || 'Paketets förare';
      const otherName = other.fieldTesterName || 'Motparten';
      const myCar = mine.registrationNumber || myLeg.registrationNumber;
      const otherCar = mine.returnRegistrationNumber || other.registrationNumber;
      const halves: MapSegment[] = [
        {
          from: splitSwap.myFrom,
          to: meetingPoint,
          color: LEG_COLOR,
          badge: myCar,
          title: `${myName} kör ${myCar || 'eget fordon'}: ${mine.fromCity} → ${swap!.pointCity}`,
        },
        {
          from: meetingPoint,
          to: splitSwap.partnerTo,
          color: LEG_COLOR,
          badge: otherCar,
          title: `${myName} kör ${otherCar || 'motpartens fordon'}: ${swap!.pointCity} → ${mine.returnToCity}`,
        },
        {
          from: splitSwap.partnerFrom,
          to: meetingPoint,
          color: SWAP_PARTNER_COLOR,
          badge: otherCar,
          title: `${otherName} kör ${otherCar || 'eget fordon'}: ${other.fromCity} → ${swap!.pointCity}`,
        },
        {
          from: meetingPoint,
          to: splitSwap.myTo,
          color: SWAP_PARTNER_COLOR,
          badge: myCar,
          title: `${otherName} kör ${myCar || 'paketets fordon'}: ${swap!.pointCity} → ${mine.toCity}`,
        },
      ];
      segments.push(...halves);
    } else {
      pkg.legs.forEach((leg, index) => {
        const from = toLatLng(leg.fromLatitude, leg.fromLongitude);
        const to = toLatLng(leg.toLatitude, leg.toLongitude);
        if (!from || !to) return;

        segments.push({
          from,
          to,
          color: LEG_COLOR,
          badge: numbered ? String(index + 1) : undefined,
          title: `Ben ${index + 1}: ${leg.fromCity} → ${leg.toCity}`,
        });
      });
    }

    // ── Valda lucka-sträckor: streckade, egen färg, egna pilar ────────────────────────
    const selection = selections?.[pkg.packageIndex];
    const candidate =
      selection?.manualCandidate ??
      pkg.candidates.find((c) => c.userId === selection?.candidateUserId) ??
      pkg.candidates[0];

    if (selection && candidate) {
      const startPoint = toLatLng(candidate.startLatitude, candidate.startLongitude);
      const endPoint = toLatLng(candidate.endLatitude, candidate.endLongitude);

      const firstGap = gapLegFor(
        selection.firstGapChoice,
        candidate.firstLegGap,
        startPoint,
        path[0] ?? null
      );
      if (firstGap) segments.push(firstGap);

      const lastGap = gapLegFor(
        selection.lastGapChoice,
        candidate.lastLegGap,
        path[path.length - 1] ?? null,
        endPoint
      );
      if (lastGap) segments.push(lastGap);
    }

    // ── Skiftestransport som inte gick att dela: motpartens rutt, ben för ben ───────────
    if (swap && !splitSwap) {
      partner?.legs.forEach((leg) => {
        const from = toLatLng(leg.fromLatitude, leg.fromLongitude);
        const to = toLatLng(leg.toLatitude, leg.toLongitude);
        if (!from || !to) return;

        segments.push({ from, to, color: SWAP_PARTNER_COLOR, strokeOpacity: 0.75 });
      });
    }

    // ── Bågarna ──────────────────────────────────────────────────────────────────────
    // Varje sträcka böjer av åt höger om sin egen färdriktning (se route-map-geometry.ts).
    // Bågdjupet räknas över alla sträckor tillsammans, så att en lucka-sträcka tillbaka längs
    // benet separeras på samma villkor som ett returben — och ett ensamt ben ritas nästan rakt.
    const bulges = arcBulges(segments);
    const bounds = new google.maps.LatLngBounds();

    segments.forEach((segment, index) => {
      const arc = arcPath(segment.from, segment.to, bulges[index]);
      // Hela bågen, inte bara ändpunkterna — annars klipper fitBounds krönet.
      arc.forEach((point) => bounds.extend(point));

      // Riktningspilar: linjerna är schematiska och utan dem går det inte att se vilken väg
      // paketet körs. Bågen bär riktningen mellan pilarna.
      const arrow = {
        icon: {
          path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 2.6,
          strokeColor: segment.color,
          strokeWeight: 1.5,
          fillColor: segment.color,
          fillOpacity: 1,
        },
        offset: segment.dashed ? '35%' : '0',
        repeat: segment.dashed ? '110px' : '90px',
      };

      overlaysRef.current.push(
        new google.maps.Polyline({
          map,
          path: arc,
          strokeColor: segment.color,
          // Streckad sträcka ritas helt med ikoner, så själva linjen är osynlig.
          strokeOpacity: segment.dashed ? 0 : segment.strokeOpacity ?? 0.9,
          strokeWeight: 3,
          icons: segment.dashed
            ? [
                {
                  icon: {
                    path: 'M 0,-1 0,1',
                    strokeColor: segment.color,
                    strokeOpacity: 0.9,
                    strokeWeight: 3,
                    scale: 2,
                  },
                  offset: '0',
                  repeat: '12px',
                },
                arrow,
              ]
            : [arrow],
        })
      );

      // Brickan sitter på bågens krön — benets råa mittpunkt ligger numera bredvid linjen,
      // och två ben i samma korridor hade delat den punkten.
      if (segment.badge) {
        // Ett bennummer får en rund bricka; ett regnummer en avlång i förarens färg.
        const isNumber = /^\d+$/.test(segment.badge);
        const halfWidth = segment.badge.length * 3.6 + 6;
        overlaysRef.current.push(
          new google.maps.Marker({
            map,
            // Regnummerbrickorna på ett skifte sitter en bit in från sträckans start i stället
            // för på krönet: tur och retur i samma korridor ligger så nära att två brickor på
            // krönen täcker varandra, medan de här hamnar i var sin ände.
            position: isNumber ? arcApex(arc) : arc[Math.round((arc.length - 1) * 0.3)],
            label: {
              text: segment.badge,
              color: '#fff',
              fontSize: isNumber ? '11px' : '10px',
              fontWeight: '600',
            },
            icon: isNumber
              ? {
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 9,
                  fillColor: '#444',
                  fillOpacity: 1,
                  strokeColor: '#fff',
                  strokeWeight: 1.5,
                }
              : {
                  path: `M ${-halfWidth},-8 H ${halfWidth} V 8 H ${-halfWidth} Z`,
                  scale: 1,
                  fillColor: segment.color,
                  fillOpacity: 1,
                  strokeColor: '#fff',
                  strokeWeight: 1.5,
                },
            title: segment.title,
            zIndex: 5,
          })
        );
      }
    });

    // ── Bytespunkten: varken start eller slut för någon av bilarna ────────────────────
    if (swap && meetingPoint) {
      bounds.extend(meetingPoint);
      overlaysRef.current.push(
        new google.maps.Marker({
          map,
          position: meetingPoint,
          // Egen symbol — bytespunkten är varken start eller slut för någon av bilarna.
          label: { text: '⇄', color: SWAP_PARTNER_COLOR, fontSize: '13px', fontWeight: '700' },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#fff',
            fillOpacity: 1,
            strokeColor: SWAP_PARTNER_COLOR,
            strokeWeight: 3,
          },
          title: `Fordonsbyte: ${swap.pointName}, ${swap.pointCity}`,
          zIndex: 10,
        })
      );
    }

    // Motpartens ändpunkter — mindre och i motpartens färg, så att paketets egen start och slut
    // fortfarande är de som syns.
    if (splitSwap && sides) {
      [
        { point: splitSwap.partnerFrom, title: `Start för ${sides.other.fieldTesterName || 'motparten'}` },
        { point: splitSwap.myTo, title: `Slutmål för ${sides.mine.registrationNumber || myLeg.registrationNumber || 'paketets fordon'}` },
      ].forEach(({ point, title }) => {
        bounds.extend(point);
        overlaysRef.current.push(
          new google.maps.Marker({
            map,
            position: point,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 5,
              fillColor: SWAP_PARTNER_COLOR,
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 1,
            },
            title,
          })
        );
      });
    }

    path.forEach((point, index) => {
      bounds.extend(point);
      const isStart = index === 0;
      const isEnd = index === path.length - 1;
      overlaysRef.current.push(
        new google.maps.Marker({
          map,
          position: point,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: isStart || isEnd ? 6 : 4,
            fillColor: isStart ? 'rgb(0,150,0)' : isEnd ? '#000' : LEG_COLOR,
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 1,
          },
          title: isStart ? 'Start' : isEnd ? 'Slut' : undefined,
        })
      );
    });

    map.fitBounds(bounds, 40);
  }, [packages, hoveredPackageIndex, selections, driverSwaps]);

  // Legenden visas bara när skiftet faktiskt ritas delat — annars beskriver den halvor som inte
  // finns på kartan.
  const legend = useMemo(() => {
    if (hoveredPackageIndex === null) return null;
    const swap = findSwap(driverSwaps, hoveredPackageIndex);
    const pkg = packages.find((p) => p.packageIndex === hoveredPackageIndex);
    if (!swap || !pkg) return null;
    const { mine, other } = swapSidesFor(swap, hoveredPackageIndex);
    const partner = packages.find((p) => p.packageIndex === other.packageIndex);
    const meetingPoint = toLatLng(swap.pointLatitude, swap.pointLongitude);
    return splitSwapPoints(pkg, partner, meetingPoint) ? { swap, mine, other } : null;
  }, [driverSwaps, hoveredPackageIndex, packages]);

  if (!scriptLoaded) return null;

  return (
    <Wrapper>
      <MapFrame>
        <MapContainer ref={containerRef} />
        {legend && (
          <SwapLegend>
            <SwapLegendRow>
              <Swatch color={LEG_COLOR} />
              {legend.mine.fieldTesterName || 'Paketets förare'}: {legend.mine.fromCity} →{' '}
              {legend.swap.pointCity} → {legend.mine.returnToCity}
            </SwapLegendRow>
            <SwapLegendRow>
              <Swatch color={SWAP_PARTNER_COLOR} />
              {legend.other.fieldTesterName || 'Motparten'}: {legend.other.fromCity} →{' '}
              {legend.swap.pointCity} → {legend.other.returnToCity}
            </SwapLegendRow>
          </SwapLegend>
        )}
      </MapFrame>
      <Hint>
        Hovra över ett paket för att se dess rutt — linjerna är schematiska, inte vägnätet.
        Varje ben bågnar åt höger i sin färdriktning, så tur och retur i samma korridor hamnar
        på var sin sida. Pilarna visar körriktningen. Grön punkt = start, svart = slut. Streckat = valt
        lucka-alternativ: blått för persontransport, grönt för annonsbil eller bil åt rätt håll.
        Skiftestransport delas vid bytespunkten (⇄): orange = paketets förare, lila =
        motparten, och regnumret på varje halva visar vilket fordon som körs.
      </Hint>
    </Wrapper>
  );
};

export default RouteMap;
